import axios from 'axios';
import { COURIER_API_BASE_URL } from '../config';

const API_BASE_URL = COURIER_API_BASE_URL;

export interface ValidateTokenResponse {
  valid: boolean;
  applicantEmail?: string;
  applicantName?: string;
  error?: string;
}

export interface PresignedUrlResponse {
  presignedUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface ConfirmUploadRequest {
  token: string;
  s3Key: string;
  documentType: string;
}

export interface ConfirmUploadResponse {
  success: boolean;
  message: string;
  error?: string;
}

export interface InterviewSlot {
  start: string;
  end: string;
  displayTime: string;
}

export interface AvailableTimesResponse {
  availableSlots: InterviewSlot[];
  error?: string;
}

export interface BookInterviewRequest {
  token: string;
  interviewTime: string;
}

export interface BookInterviewResponse {
  success: boolean;
  message: string;
  error?: string;
}

/**
 * Validate a document upload token
 */
export const validateDocumentToken = async (token: string): Promise<ValidateTokenResponse> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/documents/validate-token`, {
      params: { token },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return { valid: false, error: error.response.data.error || 'Token validation failed' };
    }
    return { valid: false, error: 'Network error validating token' };
  }
};

/**
 * Get a presigned URL for uploading a document to S3
 */
export const getPresignedUrl = async (
  token: string,
  documentType: string,
  fileName: string
): Promise<PresignedUrlResponse> => {
  const response = await axios.get(`${API_BASE_URL}/documents/generate-presigned-url`, {
    params: { token, documentType, fileName },
  });
  return response.data;
};

/**
 * Confirm that a document was uploaded successfully
 */
export const confirmUpload = async (data: ConfirmUploadRequest): Promise<ConfirmUploadResponse> => {
  const response = await axios.post(`${API_BASE_URL}/documents/confirm-upload`, data);
  return response.data;
};

/**
 * Get available interview time slots
 */
export const getAvailableInterviewTimes = async (token: string): Promise<AvailableTimesResponse> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/interview/available-times`, {
      params: { token },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return { availableSlots: [], error: error.response.data.error || 'Failed to get available times' };
    }
    return { availableSlots: [], error: 'Network error getting available times' };
  }
};

/**
 * Book an interview time slot
 */
export const bookInterview = async (data: BookInterviewRequest): Promise<BookInterviewResponse> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/interview/book`, data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return { success: false, message: '', error: error.response.data.error || 'Failed to book interview' };
    }
    return { success: false, message: '', error: 'Network error booking interview' };
  }
};
